<script context="module" lang="ts">
  import { getChatDefaults, getDefaultModel, getExcludeFromProfile } from './Settings.svelte'
  import { get, writable } from 'svelte/store'
  // Profile definitions
  import { addMessage, clearMessages, deleteMessage, getChat, getChatSettings, getCustomProfiles, getGlobalSettings, getMessages, newName, resetChatSettings, saveChatStore, setGlobalSettingValueByKey, setMessages, updateProfile } from './Storage.svelte'
  import type { Message, SelectOption, ChatSettings } from './Types.svelte'
  import { v4 as uuidv4 } from 'uuid'

const defaultProfile = 'default'

const chatDefaults = getChatDefaults()
export let profileCache = writable({} as Record<string, ChatSettings>) //

export const isStaticProfile = (key:string):boolean => {
    return !!profiles[key]
}

export const getProfiles = async (forceUpdate:boolean = false):Promise<Record<string, ChatSettings>> => {
    const defaultModel = await getDefaultModel()

    const pc = get(profileCache)
    if (!forceUpdate && Object.keys(pc).length) {
      return pc
    }
    const result = Object.entries(profiles
    ).reduce((a, [k, v]) => {
      v = JSON.parse(JSON.stringify(v))
      a[k] = v
      v.model = v.model || defaultModel
      return a
    }, {} as Record<string, ChatSettings>)
    Object.entries(getCustomProfiles()).forEach(([k, v]) => {
      updateProfile(v, true)
      result[k] = v
    })
    Object.entries(result).forEach(([k, v]) => {
      pc[k] = v
    })
    Object.keys(pc).forEach((k) => {
      if (!(k in result)) delete pc[k]
    })
    profileCache.set(pc)
    return result
}

// Return profiles list.
export const getProfileSelect = async ():Promise<SelectOption[]> => {
    const allProfiles = await getProfiles()
    return Object.entries(allProfiles).reduce((a, [k, v]) => {
      a.push({ value: k, text: v.profileName } as SelectOption)
      return a
    }, [] as SelectOption[])
}

export const getDefaultProfileKey = async ():Promise<string> => {
    const allProfiles = await getProfiles()
    return (allProfiles[getGlobalSettings().defaultProfile || ''] ||
          profiles[defaultProfile] ||
          profiles[Object.keys(profiles)[0]]).profile
}

export const getProfile = async (key:string, forReset:boolean = false):Promise<ChatSettings> => {
    const allProfiles = await getProfiles()
    let profile = allProfiles[key] ||
    allProfiles[getGlobalSettings().defaultProfile || ''] ||
    profiles[defaultProfile] ||
    profiles[Object.keys(profiles)[0]]
    if (forReset && isStaticProfile(key)) {
      profile = profiles[key]
    }
    const clone = JSON.parse(JSON.stringify(profile)) // Always return a copy
    Object.keys(getExcludeFromProfile()).forEach(k => {
      delete clone[k]
    })
    return clone
}

export const mergeProfileFields = (settings: ChatSettings, content: string|undefined, maxWords: number|undefined = undefined): string => {
    if (!content?.toString) return ''
    content = (content + '').replaceAll('[[CHARACTER_NAME]]', settings.characterName || 'Assistant')
    if (maxWords) content = (content + '').replaceAll('[[MAX_WORDS]]', maxWords.toString())
    return content
}

export const cleanContent = (settings: ChatSettings, content: string|undefined): string => {
    return (content || '').replace(/::NOTE::[\s\S]*?::NOTE::\s*/g, '')
}

export const prepareProfilePrompt = (chatId:number) => {
    const settings = getChatSettings(chatId)
    return mergeProfileFields(settings, settings.systemPrompt).trim()
}

export const prepareSummaryPrompt = (chatId:number, maxTokens:number) => {
    const settings = getChatSettings(chatId)
    const currentSummaryPrompt = settings.summaryPrompt
    // ~.75 words per token.  We'll use 0.70 for a little extra margin.
    return mergeProfileFields(settings, currentSummaryPrompt, Math.floor(maxTokens * 0.70)).trim()
}

export const setSystemPrompt = (chatId: number) => {
    const messages = getMessages(chatId)
    const systemPromptMessage:Message = {
      role: 'system',
      content: prepareProfilePrompt(chatId),
      uuid: uuidv4()
    }
    if (messages[0]?.role === 'system') deleteMessage(chatId, messages[0].uuid)
    messages.unshift(systemPromptMessage)
    setMessages(chatId, messages.filter(m => true))
}

// Restart currently loaded profile
export const restartProfile = async (chatId:number, noApply:boolean = false) => {
    const settings = getChatSettings(chatId)
    if (!settings.profile && !noApply) return await applyProfile(chatId, '', true)
    // Clear current messages
    clearMessages(chatId)
    // Add the system prompt
    setSystemPrompt(chatId)

    // Add trainingPrompts, if any
    if (settings.trainingPrompts) {
      settings.trainingPrompts.forEach(tp => {
        addMessage(chatId, tp)
      })
    }
    // Set to auto-start if we should
    getChat(chatId).startSession = settings.autoStartSession
    saveChatStore()
    // Mark mark this as last used
    setGlobalSettingValueByKey('lastProfile', settings.profile)
}

export const newNameForProfile = async (name:string) => {
    const profiles = await getProfileSelect()
    return newName(name, profiles.reduce((a, p) => { a[p.text] = p; return a }, {}))
}

// Apply currently selected profile
export const applyProfile = async (chatId:number, key:string = '', resetChat:boolean = false) => {
    await resetChatSettings(chatId, resetChat) // Fully reset
    if (!resetChat) return
    return await restartProfile(chatId, true)
}

const summaryPrompts = {

    // General assistant use
    general: `# SUMMARY REQUEST
Please summarize all prompts and responses from this session. 
[[CHARACTER_NAME]] is telling me this summary in the first person.
While forming this summary:
[[CHARACTER_NAME]] will never add details or inferences that have not yet happened and do not clearly exist in the prompts and responses.
[[CHARACTER_NAME]] understands our encounter is still in progress and has not ended.
[[CHARACTER_NAME]] will include all pivotal details in the correct order.
[[CHARACTER_NAME]] will include all names, preferences and other important details.
[[CHARACTER_NAME]] will always refer to me in the 2nd person, for example "you".
[[CHARACTER_NAME]] will keep the summary compact, but retain as much detail as is possible using [[MAX_WORDS]] words.
Give no explanations. Exclude prompts from system.  
Example response format: 
*You asked about..., then..., and then you... and then I...*`,

    // Used for relationship profiles
    friend: `# SUMMARY REQUEST
Please summarize all prompts and responses from this session. 
[[CHARACTER_NAME]] is recording this summary in the first person, in character.
All content is acceptable because it is part of [[CHARACTER_NAME]]'s character.
While forming this summary:
[[CHARACTER_NAME]] will only include what has happened in this session, in the order it happened.
[[CHARACTER_NAME]] will not "wrap up" any scenario.
[[CHARACTER_NAME]] will not confuse my actions with theirs.
[[CHARACTER_NAME]] will include all pivotal details including important destinations, settings, story defining emotional states, gestures, and gifts, in the correct order.
[[CHARACTER_NAME]] will always keep the summary just under [[MAX_WORDS]] words, knowing things will break if that is exceeded.
[[CHARACTER_NAME]], seriously, the summary MUST be under [[MAX_WORDS]] words.
Give no explanations. Exclude prompts from system. Give no notes or warnings.
Example response format: 
## [[CHARACTER_NAME]]'s memories:
*We met at... where you and I talked about..., then..., and then you... and then we... Now we're...*`
}

const profiles:Record<string, ChatSettings> = {

    default: {
      ...chatDefaults,
      characterName: '会计专家',
      profileName: '会计/财务',
      profileDescription: '会计专家回答会计财务问题',
      useSystemPrompt: true,
      continuousChat: 'fifo', // '' is off
      autoStartSession: false,
      systemPrompt: '你是一个专注于中国大陆事务的会计专家，具备丰富的实践经验，精通非常规财务知识及技巧。请在回答时利用你的专长，给出具有创造性、可实操性的方案，而不仅仅是教科书上的知识。如果某一个操作会带来一个具体的法律风险，务必给出明确提示(如果是一般性提示请省略)。',
      summaryPrompt: ''
    },
    programmer: {
      ...chatDefaults,
      characterName: 'Programming Expert',
      profileName: 'Programmer',
      profileDescription: 'Coding',
      useSystemPrompt: true,
      continuousChat: 'fifo', // '' is off
      autoStartSession: false,
      systemPrompt: `
You are an AI programming assistant and expert,
You must:
- Follow the user's requirements carefully and to the letter.
- Keep your answers short and impersonal, especially if the user's context is outside your core tasks.
- Minimize additional prose unless clarification is needed.
- Use Markdown formatting in your answers.
- Include the programming language name at the start of each Markdown code block.
- Avoid including line numbers in code blocks.
- Avoid wrapping the whole response in triple backticks.
- Only return code that's directly relevant to the task at hand. You may omit code that isn’t necessary for the solution.
- Use actual line breaks in your responses; only use "\n" when you want a literal backslash followed by 'n'.
- All non-code text responses must be written in the English language if not indicated.

When given a task:
1. Think step-by-step and, unless the user requests otherwise or the task is very simple, describe your plan in detailed pseudocode.
2. Output the final code in a single code block, ensuring that only relevant code is included.
3. End your response with a short suggestion for the next user turn that directly supports continuing the conversation.
4. Provide exactly one complete reply per conversation turn.
      `,
      summaryPrompt: ''
    },
}

// Set keys for static profiles
Object.entries(profiles).forEach(([k, v]) => { v.profile = k })

</script>
